import { useCallback, useEffect, useState } from 'react'
import { Button, Description, FieldError, Input, Label, Popover, TextArea, TextField } from '@heroui/react'
import { api } from '../../lib/api'
import { branchesToSend, clockOf, describeSchedule, minuteOf, outsideDomains, parseRecipients, pickedOf, scheduleBanner, timeHint } from '../../lib/reportSchedules'
import { explain } from '../../lib/staffErrors'
import type { Branch, ReportFrequency, ReportSchedule, ScheduleDraft, ScheduleFormat, ScheduleList } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { BranchChecks } from '../BranchChecks'
import { Choice, Notice, Pick, ReportModal } from '../kit'
import type { ToastFn } from './ReportsPage'

const FREQUENCIES: ReportFrequency[] = ['daily', 'weekly', 'monthly']

/** The Schedule button: this report's letters, Pause/Resume, edit, delete, and New schedule. */
export function SchedulesButton({ reportId, branches, toast }: { reportId: string; branches: Branch[]; toast: ToastFn }) {
  const { lang, say } = useStaffText()
  const [open, setOpen] = useState(false)
  const [list, setList] = useState<ScheduleList | null>(null)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<ReportSchedule | 'new' | null>(null)

  const load = useCallback(async () => {
    try {
      setList(await api.reportSchedules())
      setError('')
    } catch (err) {
      setError(explain(err, lang, 'reportMail.notLoaded'))
    }
  }, [lang])
  useEffect(() => {
    if (open) void load()
  }, [open, load])

  const mine = (list?.schedules ?? []).filter((s) => s.viewId === reportId)
  const rollover = (id?: string) => (id ? (branches.find((b) => b.id === id)?.rolloverMinute ?? 0) : 0)
  const place = (s: ReportSchedule) =>
    (s.branchNames?.length ?? 0) > 1
      ? s.branchNames!.join(', ')
      : s.branchId
        ? (s.branchName ?? branches.find((b) => b.id === s.branchId)?.name ?? '')
        : say('reports.everyBranch')

  const toggle = async (s: ReportSchedule) => {
    try {
      await api.updateReportSchedule(s.id, { ...draftOf(s), enabled: !s.enabled })
      toast(say(s.enabled ? 'pivot.paused' : 'pivot.resumed'))
      void load()
    } catch (err) {
      toast(explain(err, lang, 'common.unreachable'), 'error')
    }
  }
  const remove = async (s: ReportSchedule) => {
    try {
      await api.deleteReportSchedule(s.id)
      toast(say('pivot.scheduleDeleted'))
      void load()
    } catch (err) {
      toast(explain(err, lang, 'common.unreachable'), 'error')
    }
  }

  return (
    <>
      <Popover isOpen={open} onOpenChange={setOpen}>
        <Button variant="secondary" size="sm">
          <Icon name="clock" size={16} />
          {say('pivot.schedule')}
        </Button>
        <Popover.Content placement="bottom end" className="rp-pop rp-pop--sched">
          <Popover.Dialog aria-label={say('pivot.schedules')}>
            <div className="rp-sched-head">
              <b>{say('pivot.schedules')}</b>
              <Button
                variant="secondary"
                size="sm"
                onPress={() => {
                  setOpen(false)
                  setEditing('new')
                }}
              >
                <Icon name="plus" size={14} />
                {say('pivot.newSchedule')}
              </Button>
            </div>
            {error ? (
              <p className="rp-sched-empty">{error}</p>
            ) : !list ? (
              <p className="rp-sched-empty">{say('pivot.loading')}</p>
            ) : mine.length === 0 ? (
              <p className="rp-sched-empty">{say('pivot.noSchedules')}</p>
            ) : (
              mine.map((s) => {
                const banner = scheduleBanner(s)
                return (
                  <div key={s.id} className="rp-sched">
                    <Icon name="clock" size={16} />
                    <div className="rp-grow">
                      <b>{place(s)}</b>
                      <small>
                        {describeSchedule(s, lang, rollover(s.branchId))} · {s.format === 'xlsx' ? say('reports.excel') : say('reports.csv')}
                        {s.enabled ? '' : ` · ${say('pivot.pausedWord')}`}
                      </small>
                      {banner && <small className="rp-sched-warn">{say(banner.key, banner.vars)}</small>}
                    </div>
                    <Button variant="ghost" size="sm" onPress={() => void toggle(s)}>
                      {say(s.enabled ? 'pivot.pause' : 'pivot.resume')}
                    </Button>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label={say('pivot.editSchedule')}
                      onPress={() => {
                        setOpen(false)
                        setEditing(s)
                      }}
                    >
                      <Icon name="edit" size={15} />
                    </Button>
                    <Button isIconOnly variant="ghost" size="sm" aria-label={say('pivot.deleteSchedule')} onPress={() => void remove(s)}>
                      <Icon name="trash" size={15} />
                    </Button>
                  </div>
                )
              })
            )}
          </Popover.Dialog>
        </Popover.Content>
      </Popover>
      {editing && (
        <ScheduleDialog
          reportId={reportId}
          schedule={editing === 'new' ? null : editing}
          branches={branches}
          list={list}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null)
            toast(say(created ? 'pivot.scheduleCreated' : 'pivot.scheduleSaved'))
            void load()
          }}
        />
      )}
    </>
  )
}

function draftOf(s: ReportSchedule): ScheduleDraft {
  return { branchId: s.branchId, kind: s.kind, viewId: s.viewId, format: s.format, frequency: s.frequency, sendMinute: s.sendMinute, recipients: s.recipients, enabled: s.enabled }
}

/** New or edit schedule: where, how often, when, which file, and to whom. */
export function ScheduleDialog({
  reportId,
  schedule,
  branches,
  list,
  onClose,
  onSaved,
}: {
  reportId: string
  schedule: ReportSchedule | null
  branches: Branch[]
  list: ScheduleList | null
  onClose: () => void
  onSaved: (created: boolean) => void
}) {
  const { lang, say } = useStaffText()
  const everyBranch = list?.canAllBranches ?? false
  const listed = branches.map((b) => b.id)
  const [picked, setPicked] = useState<string[]>(() => (schedule ? pickedOf(schedule, listed) : listed))
  const branchId = picked.length === 1 ? picked[0]! : ''
  const [frequency, setFrequency] = useState<ReportFrequency>(schedule?.frequency ?? 'weekly')
  const [time, setTime] = useState(clockOf(schedule?.sendMinute ?? 8 * 60))
  const [format, setFormat] = useState<ScheduleFormat>(schedule?.format === 'csv' ? 'csv' : 'xlsx')
  const [recipients, setRecipients] = useState((schedule?.recipients ?? []).join(', '))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const minute = minuteOf(time)
  const rollover = branches.find((b) => b.id === branchId)?.rolloverMinute ?? 0
  const parsed = parseRecipients(recipients)
  const outside = outsideDomains(parsed.addresses, list?.allowedDomains ?? [])

  const submit = async () => {
    if (minute === null) {
      setError(say('pivot.timeNeeded'))
      return
    }
    if (parsed.problem) {
      setError(say(parsed.problem, parsed.vars))
      return
    }
    setBusy(true)
    setError('')
    const body: ScheduleDraft = { branchIds: branchesToSend(picked, listed, everyBranch), kind: 'view', viewId: reportId, format, frequency, sendMinute: minute, recipients: parsed.addresses, enabled: schedule?.enabled ?? true }
    try {
      if (schedule) await api.updateReportSchedule(schedule.id, body)
      else await api.createReportSchedule(body)
      onSaved(!schedule)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      setBusy(false)
    }
  }

  return (
    <ReportModal
      title={say(schedule ? 'pivot.editSchedule' : 'pivot.newSchedule')}
      onClose={onClose}
      onSubmit={() => {
        if (!busy && picked.length > 0) void submit()
      }}
      footer={(form) => (
        <>
          <Button variant="tertiary" size="sm" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" form={form} variant="primary" size="sm" isDisabled={busy || picked.length === 0}>
            {say(schedule ? 'common.save' : 'pivot.create')}
          </Button>
        </>
      )}
    >
      {error && <Notice>{error}</Notice>}
      <div className="rp-field">
        <span className="rp-field__label" id="rp-sched-branches">
          {say('pivot.branches')}
        </span>
        <BranchChecks branches={branches} picked={picked} onChange={setPicked} labelledBy="rp-sched-branches" />
      </div>
      <div className="rp-field">
        <span className="rp-field__label">{say('reportMail.file')}</span>
        <Pick
          className="rp-field--narrow"
          label={say('reportMail.file')}
          value={format}
          onChange={(v) => setFormat(v as ScheduleFormat)}
          options={[
            { value: 'xlsx', label: say('reports.excel') },
            { value: 'csv', label: say('reports.csv') },
          ]}
        />
      </div>
      <div className="rp-field">
        <span className="rp-field__label">{say('reportMail.frequency')}</span>
        <Choice horizontal label={say('reportMail.frequency')} value={frequency} onChange={setFrequency} options={FREQUENCIES.map((f) => ({ value: f, label: say(`reportMail.${f}`), tip: say(`pivot.covers.${f}`) }))} />
      </div>
      <TextField className="rp-field--narrow" value={time} onChange={setTime}>
        <Label title={timeHint(!!branchId, minute, rollover, lang)}>{say('reportMail.time')}</Label>
        <Input type="time" />
        {/* Said only when the branch's late day-end moves the letter. */}
        {timeHint(!!branchId, minute, rollover, lang) !== timeHint(!!branchId, null, rollover, lang) && <Description>{timeHint(!!branchId, minute, rollover, lang)}</Description>}
      </TextField>
      <TextField value={recipients} onChange={setRecipients} isInvalid={outside.length > 0}>
        <Label>{say('reportMail.recipients')}</Label>
        <TextArea rows={2} placeholder={say('pivot.recipientsHint')} />
        {outside.length > 0 && <FieldError>{say('reportMail.outside', { addresses: outside.join(', ') })}</FieldError>}
      </TextField>
    </ReportModal>
  )
}
